import React, { useState, useMemo } from "react";
import { TextInput, IconButton, useDropdownLayer } from "@narmi/design_system";
// we seem to have trouble loading types from classcat
// @ts-ignore
import cc from "classcat";
import { useCombobox } from "downshift";
import debounce from "lodash.debounce";
import Address from "byzantine/src/Address";

type PlaceResult = google.maps.places.PlaceResult;
type PlacesServiceStatus = google.maps.places.PlacesServiceStatus;
type AutocompletePrediction = google.maps.places.AutocompletePrediction;
type AddressType = "mailing" | "primary";
export type AddressAutocompleteUpdateHandler = (
  address: Partial<API.MaybeAddress>,
  type: AddressType,
  placeResult?: PlaceResult
) => void;

const POSTAL_CODE_LENGTH = 5;
const DEFAULT_COUNTRY_CODE = "US";

/**
 * This might seem odd, but parsing the adr_address HTML string
 * seems to be the most straightfoward way to get the address components.
 * The address_components property of the placeresult is a bit less consistent
 * in what it calls the various components and so it's harder to put the address together.
 * @param placeResult result from the google maps getDetail api
 * @returns parsed Address object
 */
function parseAddress(placeResult: google.maps.places.PlaceResult, isInternational: boolean) {
  const { adr_address, address_components } = placeResult;
  if (!adr_address) {
    console.error("address has no adr_address");
    return {};
  }
  const parse_place = document.createElement("div");
  parse_place.innerHTML = adr_address;
  let place_fields_map: { [key: string]: Exclude<keyof API.MaybeAddress, "type"> } = {
    "street-address": "street_address",
    locality: "city",
    region: "region_code",
    "postal-code": "postal_code",
  };
  const address: Partial<API.MaybeAddress> = {};
  Object.entries(place_fields_map).forEach(([google_adr_key, narmi_address_key]) => {
    const element = parse_place.querySelector(`.${google_adr_key}`);
    if (!element) {
      address[narmi_address_key] = "";
    } else if (!isInternational && narmi_address_key === "postal_code") {
      address[narmi_address_key] = element.innerHTML.substring(0, POSTAL_CODE_LENGTH);
    } else {
      address[narmi_address_key] = element.innerHTML;
    }
  });
  if (isInternational) {
    const countryComponent = address_components?.find((component) =>
      component.types.includes("country")
    );
    address.country_code = countryComponent?.short_name || "";
  } else {
    address.country_code = DEFAULT_COUNTRY_CODE;
  }
  return address;
}

interface Props {
  onUpdate: AddressAutocompleteUpdateHandler;
  onBlur: CallableFunction;
  value: string;
  onChange: CallableFunction;
  label: string;
  error: string;
  streetAddressLabel?: string;
  address_type?: string;
  countries?: string[]; // ISO country codes to restrict search to
  isInternational?: boolean;
}

export default function AddressAutocompleteInput({
  onUpdate,
  value,
  onChange,
  onBlur,
  label,
  error,
  streetAddressLabel,
  address_type,
  countries,
  isInternational = false,
}: Props) {
  const [places, setPlaces] = useState<AutocompletePrediction[]>([]);

  const placesService = useMemo(
    () => new window.google.maps.places.PlacesService(document.createElement("div")),
    []
  );
  // get the details on an address suggestion after it's been selected
  const getPlaceDetails = (placeId?: string) => {
    if (!placeId) return;
    const callback = (result: PlaceResult | null, status: PlacesServiceStatus) => {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
        console.error("Error code returned from the Google Maps PlacesService", status);
        return;
      }
      if (!result) {
        console.error("Place not found on the Google Maps PlacesService");
        return;
      }
      const address = parseAddress(result, isInternational);
      const type = address_type === "Mailing address" ? "mailing" : "primary";
      onUpdate(address, type, result);
    };
    return placesService.getDetails({ placeId }, callback);
  };

  // debounced function to get suggestions from user input
  const getPlaces = useMemo(() => {
    const callback = (
      predictions: AutocompletePrediction[] | null,
      status: PlacesServiceStatus
    ) => {
      if (status != window.google.maps.places.PlacesServiceStatus.OK) {
        console.error("Error code returned from the Google Maps AutocompleteService", status);
        return;
      }
      if (!predictions) {
        return;
      }
      setPlaces(predictions);
    };
    const autocompleteService = new window.google.maps.places.AutocompleteService();
    const getPlaces = (searchValue: string) => {
      const request: google.maps.places.AutocompletionRequest = {
        input: searchValue,
      };
      // Restrict to specific countries if provided
      if (countries && countries.length > 0) {
        request.componentRestrictions = { country: countries };
      }
      autocompleteService.getPlacePredictions(request, callback);
    };
    return debounce(getPlaces, 300);
  }, []);

  const { isOpen, closeMenu, getMenuProps, getInputProps, highlightedIndex, getItemProps, reset } =
    useCombobox({
      items: places,
      itemToString: (p) => p?.description || "",
      inputValue: value,
      onInputValueChange: ({ inputValue, type, selectedItem }) => {
        if (!inputValue) {
          setPlaces([]);
          reset();
        } else if (type === useCombobox.stateChangeTypes.InputChange) {
          getPlaces(inputValue);
        } else if (
          type === useCombobox.stateChangeTypes.ItemClick ||
          type === useCombobox.stateChangeTypes.InputKeyDownEnter
        ) {
          onChange(inputValue);
          getPlaceDetails(selectedItem?.place_id);
          setPlaces([]);
          return;
        }
        onChange(inputValue);
      },
    });

  const showMenu = !!(isOpen && places.length && value.length);

  const { anchorProps, layerProps } = useDropdownLayer({
    isOpen: showMenu,
    setIsOpen: () => {},
    ariaPopupType: "listbox",
    placement: "bottom",
    matchWidth: true,
  });

  return (
    <div>
      <div
        {...(anchorProps as React.HTMLAttributes<HTMLDivElement> & {
          ref: React.Ref<HTMLDivElement>;
        })}
      >
        <TextInput
          label={label || streetAddressLabel}
          error={error}
          {...getInputProps({
            onBlur,
            "aria-haspopup": anchorProps["aria-haspopup"] as "listbox",
          } as Parameters<typeof getInputProps>[0])}
        />
      </div>
      <div
        className={cc(["suggestions-container", "sentry-block", { active: showMenu }])}
        {...(getMenuProps({ ref: layerProps.ref }) as React.HTMLAttributes<HTMLDivElement>)}
        style={layerProps.style as React.CSSProperties}
      >
        {showMenu && (
          <>
            <div className="title-row">
              <span>suggestions</span>
              <IconButton name="x" kind="plain" onClick={closeMenu} />
            </div>
            {places.map((suggestion, i) => {
              return (
                <div
                  className={cc([
                    "item-row",
                    {
                      highlighted: highlightedIndex === i,
                    },
                  ])}
                  key={i}
                  {...getItemProps({ item: suggestion, index: i })}
                >
                  {suggestion.description}
                </div>
              );
            })}
            <div className="footer-row">
              <img src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
